import axios from 'axios';
import { config } from '../config';

const MUTATING = new Set(['post', 'put', 'patch', 'delete']);

let csrfToken = null;
let csrfPromise = null;
let refreshPromise = null;

axios.defaults.withCredentials = true;

export async function fetchCsrfToken() {
    if (csrfToken) {
        return csrfToken;
    }
    if (!csrfPromise) {
        csrfPromise = axios
            .get(`${config.api.web}auth/csrf-token`)
            .then((res) => {
                if (res.data?.status === 'Success' && res.data.body?.csrfToken) {
                    csrfToken = res.data.body.csrfToken;
                    return csrfToken;
                }
                throw new Error('CSRF token unavailable');
            })
            .finally(() => {
                csrfPromise = null;
            });
    }
    return csrfPromise;
}

export function clearCsrfToken() {
    csrfToken = null;
}

export async function refreshSession() {
    if (!refreshPromise) {
        refreshPromise = axios
            .post(`${config.api.web}auth/refresh`)
            .then((res) => {
                if (res.data?.status === 'Success') {
                    return res.data.body;
                }
                throw new Error('Session expired');
            })
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
}

export async function logoutSession() {
    try {
        await fetchCsrfToken();
        await axios.post(`${config.api.web}auth/logout`);
    } catch (e) {
        // ignore network errors on logout
    } finally {
        clearCsrfToken();
    }
}

axios.interceptors.request.use(async (reqConfig) => {
    const method = (reqConfig.method || 'get').toLowerCase();
    if (MUTATING.has(method)) {
        const token = await fetchCsrfToken();
        reqConfig.headers = {
            ...reqConfig.headers,
            'X-CSRF-Token': token,
        };
    }
    // FormData must keep browser-generated multipart boundary.
    if (typeof FormData !== 'undefined' && reqConfig.data instanceof FormData) {
        if (typeof reqConfig.headers?.delete === 'function') {
            reqConfig.headers.delete('Content-Type');
        } else if (reqConfig.headers) {
            delete reqConfig.headers['Content-Type'];
            delete reqConfig.headers['content-type'];
        }
    }
    return reqConfig;
});

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config;
        const status = error.response?.status;

        if (
            status === 401 &&
            original &&
            !original._retry &&
            !original.url?.includes('auth/login') &&
            !original.url?.includes('auth/refresh') &&
            !original.url?.includes('auth/verify-2fa')
        ) {
            original._retry = true;
            try {
                await refreshSession();
                return axios(original);
            } catch (refreshError) {
                clearCsrfToken();
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export function sanitizeAuthUser(user) {
    if (!user || typeof user !== 'object') {
        return user;
    }
    const { token, accessToken, refreshToken, ...safe } = user;
    return safe;
}

export default axios;
